/**
 * domain-events.ts — the shared vocabulary for the in-process domain-event bus
 * (server-side publish/subscribe lives in apps/api/src/services/domain-events/events.ts;
 * this module carries only the TYPES + the catalog, so a client bundle can import the same
 * `DomainEvent` union the server compiles with — BR-12).
 *
 * Moved here from apps/api/src/services/domain-events/events.ts (internal-event-messaging /
 * realtime-reactivity) so the union is importable from a package apps/api itself is not the
 * only consumer of. apps/api/src/services/domain-events/events.ts re-exports everything here
 * so no existing importer's path changes.
 */
/**
 * The running list of domain events — the single source of truth for "what events exist".
 *
 * This is `Record<DomainEvent['type'], EventMeta>` ON PURPOSE: TypeScript will not compile if you
 * add an arm to the `DomainEvent` union without adding its catalog entry here (and vice-versa). So
 * the list can never silently drift from the code. Keep entries reference-id-only in spirit
 * (BR-JO-05) — describe the refs the event carries, never business values.
 */
export const DOMAIN_EVENT_CATALOG = {
    'lease.created': {
        tenantResolution: 'leaseId',
        description: 'Real landlord intent to create a lease was durably established — either a Bring-Your-Own ' +
            'lease was created directly (lease.service.ts createLease) or a lease-wizard document was ' +
            'actually generated for a lease (lease-generation.orchestrator.ts generate(), covering both ' +
            'the new-lease and reuse-existing-leaseId branches). Never published for the near-empty ' +
            'placeholder Lease row createDraft() creates before Step 9 (LG-22) — that is not yet real ' +
            'intent. Signals that the move-in walkthrough should be tracked for this lease.',
        producers: [
            'lease.service.ts createLease — after the lease (and its BYO tenants) are committed, post-commit',
            'lease-generation/lease-generation.orchestrator.ts generate() — after the Lease + LeaseDocument transaction commits, post-commit',
        ],
        consumers: [
            'move_in_walkthrough member trigger → ensureWorkflow (create) — specs/move-in-walkthrough.spec.ts',
        ],
        since: 'journey-orchestration-move-in-move-out-walkthrough-workflows',
        awareness: null,
    },
    'lead.decided': {
        tenantResolution: 'none',
        description: 'A lead was approved in a way that starts a lease (decision="approved_lease_started") and the ' +
            'unit↔lead MoveInLink was upserted. Signals that the move-in journey should begin.',
        producers: [
            'leasing/lead.service.ts — after upserting the MoveInLink on an approval that starts a lease',
        ],
        consumers: [
            'move_in saga trigger → ensureWorkflow (create) — specs/move-in.saga.ts',
        ],
        since: '#642',
        awareness: null,
    },
    'payment_config.method_selected': {
        tenantResolution: 'none',
        description: "A lease's payment method intent was selected or changed (ach | external | skipped | null), on " +
            'create or update of its PaymentConfig. Drives (re)activation of the payment_setup step.',
        producers: [
            'payments/payment-config.service.ts — on create AND on update of methodIntent',
        ],
        consumers: [
            'payment_setup member trigger → reactivatePaymentSetup (reactivate) — specs/payment-setup.spec.ts',
        ],
        since: '#642',
        awareness: null,
    },
    'lease.end_scheduled': {
        tenantResolution: 'leaseId',
        description: 'A landlord explicitly scheduled a move-out via scheduleMoveOut (BR-LL-08): Lease.endDate, ' +
            'moveOutScheduledAt, and moveOutReason were durably written. Published exactly once, post-commit, ' +
            'on the FIRST scheduling only — never from updateLease (ADR-LL-018) and never re-emitted on a ' +
            'pure re-schedule of an already-scheduled move-out.',
        producers: [
            'lease.service.ts scheduleMoveOut — post-commit, first-schedule only',
        ],
        consumers: [
            'lease_end saga trigger (journey-orchestration — spawned by this event and by nothing else)',
        ],
        since: 'lease-end-process',
        awareness: null,
    },
    'appointment_proposal.created': {
        tenantResolution: 'none',
        description: 'A prospect (or an operator on their behalf) proposed a showing time — either a pending ' +
            'AppointmentProposal (proposal mode) or a directly-confirmed one with its ShowingAppointment ' +
            '(direct mode). Signals that the landlord should be notified a showing was requested.',
        producers: [
            'leasing/schedule.service.ts bookSlot — after creating a pending or direct-confirmed AppointmentProposal',
        ],
        consumers: [
            'leasing-pipeline notification fan-out — services/notifications/leasing-pipeline-notifier.ts',
            'change-awareness bridge — services/change-awareness/bridge.ts',
        ],
        since: 'leasing-support iteration-14',
        awareness: { class: 'showing_requested', instanceKey: 'leadId' },
    },
    'appointment_proposal.confirmed': {
        tenantResolution: 'none',
        description: 'A landlord confirmed a pending AppointmentProposal, scheduling its ShowingAppointment. Signals ' +
            'that the prospect-facing response should be surfaced to the landlord.',
        producers: [
            'leasing/schedule.service.ts confirmProposal — after confirming a proposal and scheduling its ShowingAppointment',
        ],
        consumers: [
            'leasing-pipeline notification fan-out — services/notifications/leasing-pipeline-notifier.ts',
            'change-awareness bridge — services/change-awareness/bridge.ts',
        ],
        since: 'leasing-support iteration-14',
        awareness: { class: 'showing_response_received', instanceKey: 'leadId' },
    },
    'appointment_proposal.rejected': {
        tenantResolution: 'none',
        description: 'A landlord rejected a pending AppointmentProposal. Signals that the prospect-facing response ' +
            'should be surfaced to the landlord.',
        producers: [
            'leasing/schedule.service.ts rejectProposal — after rejecting a proposal',
        ],
        consumers: [
            'leasing-pipeline notification fan-out — services/notifications/leasing-pipeline-notifier.ts',
            'change-awareness bridge — services/change-awareness/bridge.ts',
        ],
        since: 'leasing-support iteration-14',
        awareness: { class: 'showing_response_received', instanceKey: 'leadId' },
    },
    'appointment_proposal.cancelled': {
        tenantResolution: 'none',
        description: 'A pending or confirmed AppointmentProposal was cancelled (and its ShowingAppointment, if any, ' +
            'was cancelled too). Signals that the landlord should be notified the showing is off.',
        producers: [
            'leasing/schedule.service.ts cancel — after cancelling a proposal',
            'leasing/showing.service.ts updateAppointment — after a landlord cancels a showing that ' +
                'has a linked confirmed proposal (#941)',
        ],
        consumers: [
            'leasing-pipeline notification fan-out — services/notifications/leasing-pipeline-notifier.ts',
            'change-awareness bridge — services/change-awareness/bridge.ts',
        ],
        since: 'leasing-support iteration-14',
        awareness: { class: 'showing_cancelled', instanceKey: 'leadId' },
    },
    'appointment_proposal.rescheduled': {
        tenantResolution: 'none',
        description: 'A pending AppointmentProposal was moved to a new time, or a confirmed one was superseded by a ' +
            'new pending proposal at a new time. Signals that the landlord should be notified the showing ' +
            'time changed.',
        producers: [
            'leasing/schedule.service.ts reschedule — after rescheduling a pending or confirmed proposal',
        ],
        consumers: [
            'leasing-pipeline notification fan-out — services/notifications/leasing-pipeline-notifier.ts',
            'change-awareness bridge — services/change-awareness/bridge.ts',
        ],
        since: 'leasing-support iteration-14',
        awareness: { class: 'showing_cancelled', instanceKey: 'leadId' },
    },
    'showing.scheduled': {
        tenantResolution: 'none',
        description: 'A private ShowingAppointment was placed on the calendar for a lead (standalone landlord-initiated ' +
            'scheduling, not a proposal confirmation). Signals that the landlord should be notified.',
        producers: [
            'leasing/showing.service.ts scheduleAppointment — after creating a private ShowingAppointment for a lead',
        ],
        consumers: [
            'leasing-pipeline notification fan-out — services/notifications/leasing-pipeline-notifier.ts',
            'change-awareness bridge — services/change-awareness/bridge.ts',
        ],
        since: 'leasing-support iteration-14',
        awareness: { class: 'showing_scheduled', instanceKey: 'leadId' },
    },
    'applicant.submitted': {
        tenantResolution: 'none',
        description: "An applicant completed and submitted their application, on the applicant's first submission only " +
            '(idempotent re-submit does not re-fire). Signals that the landlord should be notified.',
        producers: [
            'leasing/applicant.service.ts submit — after an applicant\'s first submission',
        ],
        consumers: [
            'leasing-pipeline notification fan-out — services/notifications/leasing-pipeline-notifier.ts',
            'change-awareness bridge — services/change-awareness/bridge.ts',
        ],
        since: 'leasing-support iteration-14',
        awareness: { class: 'applicant_submitted', instanceKey: 'leadId' },
    },
    'lead.created': {
        tenantResolution: 'none',
        description: 'A Lead was created for a listing — either through a landlord/system-actor intake path ' +
            '(leadService.createLead, both actor branches) or the inbound-email upsert (re-inquiry to ' +
            'an existing Lead re-fires it too — harmless for cache invalidation). Needed for ' +
            "unresponded-lead-messages freshness (ADR-AT-019): a brand-new lead with no prior message " +
            "isn't covered by reusing BR-7's un-responded-message query alone.",
        producers: [
            'leasing/lead.service.ts createLead — after the Lead (+Case) transaction commits, post-commit, both user and system actor branches',
            'leasing/leasing-from-message.creator.ts — returns the event as CaseAnchor.pendingEvent ' +
                "from inside its transaction (the enclosing transaction is owned by agent/comms.service.ts's " +
                "createCaseFromMessage, outside this creator's scope); comms.service.ts publishes it after " +
                '`await prisma.$transaction(...)` resolves, true post-commit from the outer transaction\'s perspective',
        ],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-09-16)',
        awareness: null,
    },
    'applicant.reviewed': {
        tenantResolution: 'none',
        description: "An Applicant's submitted application was reviewed by the landlord for the first time — " +
            'Applicant.reviewedAt was durably set (leasing-support/BR-37). Idempotent: a repeat view ' +
            'after reviewedAt is already set does not re-publish. Signals that the ' +
            'unreviewed-applications attention item for this applicant should clear (BR-AT-20).',
        producers: [
            'leasing/applicant.service.ts markReviewed — after reviewedAt is set for the first time',
        ],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-09-16)',
        awareness: null,
    },
    'case.message_received': {
        tenantResolution: 'none',
        description: 'An inbound message was routed and persisted to a case, on every ingress that can deliver one ' +
            'to a case. Substrate-generic — fires for every agentKind (leasing, repair, etc.), not ' +
            "leasing-only; the leasing-pipeline consumer re-reads the case's agentKind from the DB before " +
            'deciding whether to act (BR-NOTIF-04). `messageId` is the ChannelMessage id where the ingress ' +
            'has one (relay inbound routing, comms.service attach/create-from-message); on the portal ' +
            "reply path (no ChannelMessage involved) it is the CaseMessage id instead.",
        producers: [
            'relay/inbound-routing.service.ts persistInboundMessage — after persisting an inbound message routed to a case',
            "agent/comms.service.ts attachMessage — after attaching an inbound ChannelMessage to an existing case",
            'agent/comms.service.ts createCaseFromMessage — after creating a case from an inbound ChannelMessage',
            'case/case-message.service.ts addComment — after a counterparty (tenant/vendor) posts a message on a case',
        ],
        consumers: [
            'leasing-pipeline notification fan-out — services/notifications/leasing-pipeline-notifier.ts',
            'change-awareness bridge — services/change-awareness/bridge.ts (ephemeral Centrifugo publish only)',
        ],
        since: 'leasing-support iteration-14',
        // awareness: null — NOT "no awareness class exists". `lead_message_received` is a real,
        // leasing-only class, but this event is substrate-generic (fires for every agentKind, not
        // just leasing — see the description above), and deciding whether THIS occurrence is a
        // leasing one requires re-reading the case's agentKind from the DB (LS-BR-NOTIF-04). The
        // bridge is architecturally forbidden from doing that read (BR-11 — it is what keeps the
        // bridge universal). So the durable awareness write for this class stays with
        // `leasing-pipeline-notifier.ts`, which CAN read the DB and applies the agentKind gate; the
        // bridge still forwards the raw event to Centrifugo for UI reactivity (BR-7's universality
        // is about that ephemeral output, which needs no DB read and is unaffected).
        awareness: null,
    },
    'case.activity_recorded': {
        tenantResolution: 'none',
        description: 'A CaseEvent row was durably committed for a case, or its agent suspension state changed ' +
            '(suspend / takeover / hand-back, no CaseEvent written) (RA-ACTIVITY-EVENT, BR-CASE-ACTIVITY-EVENT). ' +
            'Signalled strictly post-commit: case-event.service emit()/emitMany() issue a transactional ' +
            'pg_notify, which Postgres delivers only on COMMIT, and a listener coalesces per case and ' +
            'publishes. Substrate-generic — every agentKind. Reference ids only — never carries event content.',
        producers: [
            'case/case-activity-publisher.ts — listener on the case_activity_recorded pg_notify channel ' +
                '(sent by case/case-event.service.ts emit/emitMany inside the writing transaction, and by ' +
                'agent/agent-suspension.service.ts via notifyCaseActivityForCase on suspension changes)',
        ],
        consumers: [
            'attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)',
            'change-awareness bridge — services/change-awareness/bridge.ts (ephemeral Centrifugo publish only)',
        ],
        since: '13a repair-agent',
        awareness: null,
    },
    'case.thread_read': {
        tenantResolution: 'none',
        description: "A manager's per-thread read cursor on a case advanced (RA-UNREAD-MARK, BR-THREAD-READ). " +
            'Lets the manager\'s other open sessions and the attention cache reflect the cleared unread ' +
            'state. Reference ids only.',
        producers: ['case/case-thread-read.service.ts markRead — after the cursor upsert resolves'],
        consumers: [
            'attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)',
            'change-awareness bridge — services/change-awareness/bridge.ts (ephemeral Centrifugo publish only)',
        ],
        since: '13a repair-agent',
        awareness: null,
    },
    'case.message_sent': {
        tenantResolution: 'none',
        description: 'An outbound message was sent on a case — the landlord/agent-side counterpart to ' +
            'case.message_received. Needed for unresponded-lead-messages freshness (ADR-AT-019): without ' +
            "it, a landlord's reply does not invalidate the attention cache and the item can read stale " +
            'until an unrelated invalidation happens to occur.',
        producers: [
            "leasing/lead.service.ts's publishMessageSent, called from " +
                'routes/manager/leasing-leads.routes.ts\'s outbound-reply handler (POST ' +
                '/landlord/api/leads/:id/messages), after commsService.sendMessage succeeds. ' +
                "agent/comms.service.ts's two other case.message_sent CaseEvent emission sites " +
                '(outbound relay send, portal reply send) do not yet publish this bus event — that ' +
                'remains a follow-up.',
        ],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-09-16)',
        awareness: null,
    },
    'case.escalation_raised': {
        tenantResolution: 'none',
        description: 'An agent escalated a question on a case and is awaiting a landlord answer — a new open ' +
            'CaseEscalation was durably written (BR-ESC-RAISE). Not re-fired while an escalation is ' +
            'already open on the case; a second raise appends to the existing question instead ' +
            '(BR-ESC-NOTIFY). Reference ids only — never carries the question text (BR-NOTIF-02, BR-NOTIF-12). ' +
            'Substrate-generic — fires for every agentKind (leasing, repair, and any future kind), not ' +
            'leasing-only (same shape as case.message_received above); the consumer re-reads the ' +
            "case's agentKind (and linked Lead/RepairRequest) to resolve a per-kind reference before " +
            'acting (BR-NOTIF-04).',
        producers: [
            'case/case-message.service.ts — addComment, agent sender with the escalation intent, post-commit',
        ],
        consumers: [
            'agent-escalation-notifier.ts — landlord escalation notification, every agent kind',
            'change-awareness bridge — services/change-awareness/bridge.ts (ephemeral Centrifugo publish only)',
        ],
        since: 'agents-landlord-escalation',
        // awareness: null — same reasoning as case.message_received directly above: this event is
        // substrate-generic and choosing the notification's reference (lead / repair_request /
        // case_escalation) requires an agentKind re-read the bridge must not perform (BR-11). The
        // domain subscriber (agent-escalation-notifier.ts) owns the durable write; the bridge still
        // forwards the event to Centrifugo (BR-7's ephemeral-output universality is unaffected).
        awareness: null,
    },
    'agent.draft_held': {
        tenantResolution: 'none',
        description: 'An AI-drafted response was held pending manager review (BR-REVIEW-HOLD) — the ChannelMessage took ' +
            'status held_for_review. Fires from both hold sites and the reminder job, once per notification ' +
            '(the initial hold and each escalating reminder). Signals that the landlord should be notified an ' +
            'agent draft is awaiting review.',
        producers: [
            'agent/agent-review-notifier.service.ts notifyHeld — after writing the ManagerNotification row for a held draft',
        ],
        consumers: [
            'change-awareness bridge — services/change-awareness/bridge.ts',
        ],
        since: 'realtime-reactivity',
        awareness: { class: 'agent_draft_held_for_review', instanceKey: 'reviewLogId' },
    },
    'agent.conversation_turn_ready': {
        tenantResolution: 'none',
        description: 'An improvement-conversation turn ended on the runtime and Resida accepted its completion ' +
            '(BR-CONV-DELIVER): lets the landlord\'s open conversation view fetch the new reply. ' +
            'Reference ids only {conversationId, turnId, landlordScopeId} — never reply text. Published ' +
            'exactly once per turn (a repeated completion publishes nothing). Reactivity only: no badge, ' +
            'no seen state, no notification row.',
        producers: [
            'agent/improvement-conversation.service.ts handleTurnCompletion — post-commit, after the defect feedback item (if any) is written',
        ],
        consumers: [
            'change-awareness bridge — services/change-awareness/bridge.ts (ephemeral Centrifugo publish only, ' +
                "personalManagerChannel of the completing landlord)",
        ],
        since: 'agents-improvement-agent',
        // awareness: null — a conversation reply is something the landlord is already looking at in
        // the conversation view; it is never a new "thing to act on", so no ManagerNotification row.
        awareness: null,
    },
    'relay.delivery_failed': {
        tenantResolution: 'none',
        description: 'An outbound relay message transitioned to status:failed. Fires from all three producing sites, ' +
            'per message, for every failure cause. Signals that the landlord should be notified a message ' +
            'failed to deliver.',
        producers: [
            'relay/delivery-failure-notifier.service.ts notifyFailed — after writing the ManagerNotification row for a failed delivery',
        ],
        consumers: [
            'change-awareness bridge — services/change-awareness/bridge.ts',
        ],
        since: 'realtime-reactivity',
        awareness: { class: 'relay_message_delivery_failed', instanceKey: 'channelMessageId' },
    },
    'email.message_received': {
        tenantResolution: 'none',
        description: 'An inbound email was persisted to a thread. Signals that the landlord should be notified of a ' +
            'new inbound email.',
        producers: [
            'email-channel/email-channel.service.ts — after persisting an inbound EmailMessage',
        ],
        consumers: [
            'change-awareness bridge — services/change-awareness/bridge.ts',
        ],
        since: 'realtime-reactivity',
        awareness: { class: 'new_inbound_email', instanceKey: 'threadId' },
    },
    'expense.review_queued': {
        tenantResolution: 'none',
        description: 'An expense entered (or re-entered) PENDING_REVIEW status — email ingest, manual create, or ' +
            'AI-assisted upload. Fires from all three PENDING_REVIEW sites. Signals that the landlord should ' +
            'be notified an expense needs review.',
        producers: [
            'expenses/expense.service.ts create / createFromUpload — after creating a PENDING_REVIEW expense',
        ],
        consumers: [
            'change-awareness bridge — services/change-awareness/bridge.ts',
        ],
        since: 'realtime-reactivity',
        awareness: { class: 'expense_review_queued', instanceKey: 'expenseId' },
    },
    'ledger.entry_posted': {
        tenantResolution: 'leaseId',
        description: 'A ledger entry (charge, credit, or reversal) was posted for a lease, after the owning ' +
            'transaction committed. Changes what a landlord owes/is owed, so the attention surface must ' +
            're-derive past-due-rent and related items for this lease.',
        producers: [
            'payments/ledger.service.ts publishLedgerEntryPosted — called after commit from ' +
                'payment.service.ts initiatePayment, ledger.service.ts createManualCharge, ' +
                'pending-credit.service.ts createCreditWithEffectiveDate/processPendingCredits, ' +
                'reconciler-charge-generation.handler.ts, reconciler-late-fee.handler.ts (ACH branch), ' +
                'and ledger-reconciliation.service.ts (after createIdempotentReversal)',
        ],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-09-13)',
        awareness: null,
    },
    'pending_late_fee.status_changed': {
        tenantResolution: 'none',
        description: "A PendingLateFee's status changed (created, approved, or dismissed) for a lease, after the " +
            'owning write committed. Changes what the pending-late-fees attention item shows.',
        producers: [
            'payments/pending-late-fee.service.ts approvePendingLateFee / dismissPendingLateFee',
            'payments/reconciler-late-fee.handler.ts runBatch — the external createPendingLateFee branch',
            'payments/payment-config.service.ts updatePaymentConfig / disablePayments — when ' +
                'dismissPendingLateFeesForLease ran',
        ],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-09-13)',
        awareness: null,
    },
    'bank_transaction.status_changed': {
        tenantResolution: 'none',
        description: "A BankTransaction's match status changed (confirmed, dismissed, manually matched, or an " +
            'ignore rule applied), after the owning write committed. Changes what the ' +
            'unmatched-transactions attention item shows.',
        producers: [
            'payments/transaction-matching.service.ts confirmMatch / dismissTransaction / manualMatch / ' +
                'submitOnboardingMatches',
            'payments/transaction-ignore-rule.service.ts createIgnoreRule — when it changed rows',
            'jobs/plaid-sync-worker.job.ts — once per run, when the sync or its matching pass changed anything',
            'routes/manager/linked-bank-accounts.routes.ts POST :id/sync — manual sync, when it changed anything',
        ],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-09-13)',
        awareness: null,
    },
    'linked_bank_account.status_changed': {
        tenantResolution: 'none',
        description: "A LinkedBankAccount's status changed (removed, its status updated, or every account for a " +
            'manager disconnected), after the owning write committed. Changes what the payment-setup and ' +
            'stripe-connect attention items show.',
        producers: [
            'payments/linked-bank-account.service.ts removeLinkedBankAccount / ' +
                'updateLinkedBankAccountStatus / disconnectAllForManager',
        ],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-09-13)',
        awareness: null,
    },
    'signing_envelope.status_changed': {
        tenantResolution: 'envelopeId',
        description: "A SigningEnvelope moved to a new lifecycle state for a manager recipient (sent, a recipient " +
            'signed, completed, cancelled, or expired), after the provider-status sync (or the write that ' +
            'caused it) committed. Changes what the awaiting-signature attention item shows.',
        producers: [
            'signing/signing-domain-events.bridge.ts — bridges signingEvents ' +
                "'signing.envelope.sent' | 'signing.recipient.signed' | 'signing.envelope.completed' | " +
                "'signing.envelope.cancelled' | 'signing.envelope.expired' onto this event, once per " +
                'distinct manager recipient',
        ],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-09-13)',
        awareness: null,
    },
    'chat.message_sent': {
        tenantResolution: 'conversationId',
        description: 'A chat message was sent into a conversation with at least one MANAGER participant, after the ' +
            'owning transaction committed. Changes what the unread-chats attention item shows for each ' +
            'such participant.',
        producers: ['chat/message.service.ts createMessage — once per MANAGER participant, after the transaction'],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-09-13)',
        awareness: null,
    },
    'chat.conversation_read': {
        tenantResolution: 'conversationId',
        description: 'A MANAGER participant marked a conversation read. Changes what the unread-chats attention ' +
            'item shows for that manager.',
        producers: ['chat/read-receipt.service.ts markRead — only when the participant is MANAGER'],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-09-13)',
        awareness: null,
    },
    'email.thread_read': {
        tenantResolution: 'none',
        description: 'An email thread was marked read for its owning account, when the mark-read write actually ' +
            'changed rows. Changes what the unread-email attention item shows.',
        producers: ['email-channel/email-channel.service.ts getThreadMessages — only when the mark-read updateMany count > 0'],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-09-13)',
        awareness: null,
    },
    'email.thread_changed': {
        tenantResolution: 'none',
        description: 'An email thread changed without a landlord alert — a spam-flagged message from an ' +
            'unrecognized sender was stored, or an unmatched thread was linked to a tenant or vendor, or ' +
            'discarded. Changes what the email attention items show.',
        producers: [
            'email-channel/email-channel.service.ts ingestInbound — spam-flagged UNMATCHED arrival',
            'email-channel/email-channel.service.ts linkThread — after the transaction',
            'email-channel/email-channel.service.ts discardThread — after the transaction',
        ],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'messaging-framework Iteration 20',
        awareness: null,
    },
    'expense.review_resolved': {
        tenantResolution: 'none',
        description: 'An expense left the review queue (confirmed, or deleted while still PENDING_REVIEW), after ' +
            'the owning transaction committed. Changes what the expense-review attention item shows.',
        producers: [
            'expenses/expense.service.ts confirmExpense (bulkConfirm inherits it) — after the transaction',
            'expenses/expense.service.ts delete — after the transaction, only when the deleted expense ' +
                'was PENDING_REVIEW',
        ],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-09-13)',
        awareness: null,
    },
    'lease.changed': {
        tenantResolution: 'leaseId',
        description: "A lease's attributes changed in a way that can affect its attention items (archived, " +
            'unarchived, a general update, a tenant added/removed, a payment-config selection made or ' +
            'cleared, or the acceptance of a tenant invite added a LeaseTenant), after the owning write ' +
            'committed.',
        producers: [
            'lease.service.ts archiveLease / unarchiveLease / updateLease / addTenant / setTenantDepartureDate',
            'tenant-invite.service.ts — where invite acceptance adds a LeaseTenant',
            'payments/payment-config.service.ts enablePayments / disablePayments — after their transaction; ' +
                'updatePaymentConfig — after its transaction, only when methodIntent is undefined and the ' +
                'destination or payment method changed',
        ],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-09-13)',
        awareness: null,
    },
    'unit.changed': {
        tenantResolution: 'unitId',
        description: "A unit's attributes, or a resource scoped to it (a UnitDisclosure, or its condition template " +
            'rooms/items), changed, after the owning write or transaction committed; or a condition ' +
            'walkthrough on one of its leases (submit/acknowledge).',
        producers: [
            'unit.service.ts create / update / soft-delete',
            'unit-disclosure.service.ts — the UnitDisclosure upsert/update sites',
            'condition/condition-template.service.ts instantiateTemplate / createRoom / room update / ' +
                'room delete / item create/update/delete',
            'condition/condition-walkthrough.service.ts submit / acknowledge — freshness only, for the ' +
                'condition-walkthrough-pending attention provider (BR-30)',
        ],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-09-13)',
        awareness: null,
    },
    'property.changed': {
        tenantResolution: 'propertyId',
        description: "A property's attributes changed (create, or a metadata update), after the owning write committed.",
        producers: ['property.service.ts property create / metadata update'],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-09-13)',
        awareness: null,
    },
    'property_bank_account.linked': {
        tenantResolution: 'none',
        description: 'A property was linked to a bank account, after the link upsert committed.',
        producers: ['property-bank-account.service.ts — the link upsert'],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-09-13)',
        awareness: null,
    },
    'stripe_connect_account.status_changed': {
        tenantResolution: 'none',
        description: "A StripeConnectAccount's persisted status (e.g. chargesEnabled) changed, after the write " +
            'that persisted it. Changes what the stripe-connect attention item shows.',
        producers: [
            'payments/stripe-connect.service.ts getConnectAccountStatus — where it persists ' +
                'chargesEnabled, only when a status field actually changed',
        ],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-09-13)',
        awareness: null,
    },
    'funding_source.status_changed': {
        tenantResolution: 'none',
        description: "A manager's ACH funding source status changed (created, verified, failed, or removed), after " +
            'the write committed. Changes what the payment-setup attention item shows.',
        producers: ['payments/funding-source.service.ts — each create/verify/fail/remove write'],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-09-13)',
        awareness: null,
    },
    'email_ingest_address.created': {
        tenantResolution: 'none',
        description: 'An email-ingest address was created for a manager, after the create write committed.',
        producers: ['expenses/email-ingest-address.service.ts create'],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-09-13)',
        awareness: null,
    },
    'manager_credential.changed': {
        tenantResolution: 'none',
        description: "A manager's credential was created or revoked, after the write committed.",
        producers: ['credential.service.ts credential create / revoke'],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-09-13)',
        awareness: null,
    },
    'manager_profile.changed': {
        tenantResolution: 'none',
        description: "A manager's profile (name, phone, address) was updated, after the write committed.",
        producers: ['profile.service.ts updateProfile'],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-10-19)',
        awareness: null,
    },
    'tenant_invite.status_changed': {
        tenantResolution: 'none',
        description: "A tenant invite's status changed (created, cancelled, accepted, or expired), after the " +
            'owning write committed. One event per call — a batch expiry publishes once per landlord, ' +
            'never once per invite.',
        producers: ['tenant-invite.service.ts invite create / cancel / accept / expire'],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (attention-surface BR-21)'],
        since: 'attention domain (2026-09-13)',
        awareness: null,
    },
    'repair.request_created': {
        tenantResolution: 'none',
        description: 'A repair request (Case + RepairRequest, PK-inheritance) was durably created, after the ' +
            'owning transaction committed. Signals that the repair-request-triage attention provider ' +
            'should surface this ticket for a manager to categorize and assign a vendor (RA-REQUEST-EVENT).',
        producers: ['repair/repair-request.service.ts create — after the Case+RepairRequest transaction commits, post-commit'],
        consumers: [
            'attention providers — repair-request-triage (services/attention/providers/repair-request-triage.provider.ts) ' +
                'reads RepairRequest directly rather than subscribing to this event; the event exists so the ' +
                "creation fact is observable on the bus like every other case-creation event",
        ],
        since: 'agents-hitl-visibility',
        awareness: { class: 'repair_request_created', instanceKey: 'caseId' },
    }, 'listing.status_changed': {
        tenantResolution: 'none',
        description: "A listing's status changed (activate, pause, system pause, close, reopen), after the owning " +
            'transaction committed. One event per listing actually transitioned.',
        producers: [
            'leasing/listing.service.ts — every status-transition path (activate, pause, close, reopen, ' +
                'system pause, bulk pause by landlord / unit), post-commit',
        ],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (external-post takedown item)'],
        since: '2026-10',
        awareness: null,
    },
    'external_listing_post.changed': {
        tenantResolution: 'none',
        description: 'An external listing post record (Facebook Marketplace / Craigslist) was recorded, marked ' +
            'removed, or deleted, after the write committed.',
        producers: ['listing-extension/external-listing-post.service.ts record / markRemoved / remove — post-commit'],
        consumers: ['attention-invalidation.ts — busts the landlord attention cache (external-post takedown item)'],
        since: '2026-10',
        awareness: null,
    },
};
//# sourceMappingURL=domain-events.js.map